import { useEffect, useState, type FormEvent } from 'react';
import { useCreateMtoLotMutation } from '@shared/api';
import type { NewMaterialLot } from '@entities/mto';
import { apiErrorMessage } from '@entities/mto';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import { Button } from '@shared/ui/Button';
import { Input, Label } from '@shared/ui/Input';
import {
  AddLotCard,
  Form,
  FormActions,
  FormField,
  SectionTitle,
} from '../styles';

export function AddLotForm({ canWrite, onError }: { canWrite: boolean; onError: (msg: string) => void }) {
  const { activeProjectName, activeProjectId } = useActiveProjectContext();
  const [lotNo, setLotNo] = useState('');
  const [nomenclature, setNomenclature] = useState('');
  const [supplier, setSupplier] = useState('');
  const [supplyDate, setSupplyDate] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [siteId, setSiteId] = useState(() => activeProjectName || activeProjectId || '');
  const [createLot, { isLoading }] = useCreateMtoLotMutation();

  useEffect(() => {
    const fallback = activeProjectName || activeProjectId || '';
    if (fallback && !siteId) {
      setSiteId(fallback);
    }
  }, [activeProjectName, activeProjectId, siteId]);

  function reset() {
    setLotNo('');
    setNomenclature('');
    setSupplier('');
    setSupplyDate('');
    setQuantity('');
    setUnit('');
    setSiteId(activeProjectName || activeProjectId || '');
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onError('');
    if (!nomenclature.trim()) {
      onError('Укажите наименование материала — это обязательное поле.');
      return;
    }
    const body: NewMaterialLot = { nomenclature: nomenclature.trim() };
    if (lotNo.trim()) body.lot_no = lotNo.trim();
    if (supplier.trim()) body.supplier = supplier.trim();
    if (supplyDate.trim()) body.supply_date = supplyDate.trim();
    if (quantity.trim()) body.quantity = quantity.trim();
    if (unit.trim()) body.unit = unit.trim();
    if (siteId.trim()) body.site_id = siteId.trim();
    try {
      await createLot(body).unwrap();
      reset();
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось добавить партию.');
      // eslint-disable-next-line no-console
      console.error('createMtoLot failed', err);
    }
  }

  return (
    <AddLotCard>
      <SectionTitle>Добавить партию</SectionTitle>
      <Form onSubmit={handleSubmit}>
        <FormField>
          <Label htmlFor="qc-lot-no">Номер партии</Label>
          <Input id="qc-lot-no" value={lotNo} onChange={(e) => setLotNo(e.target.value)} placeholder="№ партии" />
        </FormField>
        <FormField>
          <Label htmlFor="qc-lot-nomenclature">Наименование *</Label>
          <Input
            id="qc-lot-nomenclature"
            value={nomenclature}
            onChange={(e) => setNomenclature(e.target.value)}
            placeholder="Материал"
            required
            aria-required="true"
          />
        </FormField>
        <FormField>
          <Label htmlFor="qc-lot-supplier">Поставщик</Label>
          <Input id="qc-lot-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
        </FormField>
        <FormField>
          <Label htmlFor="qc-lot-date">Дата поставки</Label>
          <Input id="qc-lot-date" type="date" value={supplyDate} onChange={(e) => setSupplyDate(e.target.value)} />
        </FormField>
        <FormField>
          <Label htmlFor="qc-lot-qty">Количество</Label>
          <Input id="qc-lot-qty" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="0" />
        </FormField>
        <FormField>
          <Label htmlFor="qc-lot-unit">Ед. изм.</Label>
          <Input id="qc-lot-unit" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="т, м³, шт" />
        </FormField>
        <FormField>
          <Label htmlFor="qc-lot-site">Объект</Label>
          <Input id="qc-lot-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} placeholder="Объект, например ДГП-2" />
        </FormField>
        <FormActions>
          <Button type="submit" $variant="primary" $size="sm" disabled={!canWrite || isLoading}>
            {isLoading ? 'Добавляю…' : 'Добавить партию'}
          </Button>
        </FormActions>
      </Form>
    </AddLotCard>
  );
}
